/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useRef, useState } from "react";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { LANG } from "~/utils/constant";
import ScrollArrowButton from "../shared-components/ScrollArrowButton";

const MandirTabs = ({
  mandir,
  setSelectedMandir,
  selectedMandir,
}: {
  mandir: { en: string; gu: string; img: string; value: string }[];
  setSelectedMandir: (mandir: string) => void;
  selectedMandir: string;
}) => {
  const lang = useLanguageParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { medium } = useFontStyle();

  const showArrows = canScrollLeft || canScrollRight;

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      const scrolledToRightEdge = scrollLeft + clientWidth >= scrollWidth - 10;
      setCanScrollRight(!scrolledToRightEdge);
    }
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 300; // Amount to scroll per click
      const value = direction === "left" ? -scrollAmount : scrollAmount;
      container.scrollBy({ left: value, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    checkScroll();

    const handleResize = () => checkScroll();
    const handleScroll = () => checkScroll();

    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const selectedTab = tabRefs.current[selectedMandir];
    const container = scrollRef.current;
    let time: NodeJS.Timeout;
    if (selectedTab && container) {
      // Small delay to ensure DOM is updated
      time = setTimeout(() => {
        // Use container.scrollLeft instead of scrollIntoView to prevent page scroll
        const tabRect = selectedTab.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollLeft = container.scrollLeft;

        // Calculate the position to scroll to center the tab
        const tabCenter = tabRect.left - containerRect.left + scrollLeft + tabRect.width / 2;
        const containerCenter = container.clientWidth / 2;
        const targetScrollLeft = tabCenter - containerCenter;

        // Scroll the container without affecting page scroll
        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth",
        });

        // Update scroll state after scrolling
        setTimeout(() => {
          checkScroll();
        }, 300);
      }, 50);
    }

    return () => {
      clearTimeout(time);
    };
  }, [selectedMandir]);

  return (
    <div className="w-full flex justify-between 2xl:justify-between items-center gap-3 px-3">
      {/* Left Arrow — only visible when canScrollLeft is true */}
      {canScrollLeft && (
        <div className="w-[2%] hidden lg:flex justify-center items-center z-[1] ml-3">
          <ScrollArrowButton direction="left" onClick={() => scroll("left")} disabled={!canScrollLeft} />
        </div>
      )}

      {/* Scrollable Tabs */}
      <div
        ref={scrollRef}
        className={cn(
          "w-full flex gap-4 overflow-x-auto scrollbar-hide justify-start items-center",
          showArrows ? "justify-start px-0" : "justify-center px-3",
        )}
      >
        {mandir.map((m) => (
          <div
            key={m.value}
            ref={(el) => (tabRefs.current[m.value] = el)}
            className={cn(
              "rounded-l-full rounded-r-full flex justify-start items-center cursor-pointer border border-textHeadingColor/30 relative",
              selectedMandir === m.value ? "bg-dailyDarshanChipColor" : "",
              m.value !== selectedMandir && "hover:bg-chipHoverColor hover:text-textColor bg-cardBg transition-all duration-300 ease-in-out",
            )}
            onClick={() => setSelectedMandir(m.value)}
          >
            <div className={cn("w-9  h-9 2xl:w-10 2xl:h-10 flex justify-center items-center rounded-full bg-sky-100")}>
              <img
                src={`/images/${m.img}.png`}
                alt={m.value}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = "/images/karelibaug.png";
                }}
                className="h-full w-full rounded-full object-cover select-none"
              />
            </div>

            <div className="pr-4 pl-3">
              <span
                title={lang === LANG.english ? m.en : m.gu}
                className={cn("body_text_s capitalize text-nowrap", selectedMandir !== m.value ? "text-textColor" : "text-textSecondary", medium)}
              >
                {lang === LANG.english ? m.en : m.gu}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Right Arrow — only visible when canScrollRight is true */}
      {canScrollRight && (
        <div className="w-[2%] hidden lg:flex justify-center items-center z-[1] mr-3 ">
          <ScrollArrowButton direction="right" onClick={() => scroll("right")} disabled={!canScrollRight} />
        </div>
      )}
    </div>
  );
};

export default MandirTabs;
